import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useParams, Navigate, useOutletContext } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { Box, useTheme, Stack, Button, ButtonBase, Popover, Typography } from '@mui/material'
import { useSites } from 'store'
import EmptyScreen from 'packages/ui/EmptyScreen'

import TextFilter from 'components/TextFilter'
import DateFilter from 'components/DateFilter/DateFilter'
import { SubHeaderInfos, SubHeaderInfosIcon } from 'components/global/SubHeader/SubHeaderStyles'
import { useUser } from 'store'
import { useRegisterBreadcrumbAction } from 'components/AuthenticatedLayout/Header/components/BreadcrumbActions'
import HeaderExportButton from 'components/global/Buttons/HeaderExportButton'

import useFilters, { useItemsSelected } from './store'
import { MainContent, TableContent } from './RanStyles'
import useRans from './use-rans'
import { canEditRans } from './permissions'
import Table from './Table/Table'

import Card from './Card/CardInMass'
import { trackEvent } from 'analytics'

const REMAINING_KEYS_POPOVER_ID = 'ran-remaining-keys-by-ssid'

const RemainingKeysBySsid = ({ bySsid, loading }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [anchorElement, setAnchorElement] = useState(null)
  const isOpen = Boolean(anchorElement)

  const handleToggle = event => setAnchorElement(isOpen ? null : event.currentTarget)
  const handleClose = () => setAnchorElement(null)

  return (
    <>
      <ButtonBase
        onClick={handleToggle}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={isOpen ? REMAINING_KEYS_POPOVER_ID : undefined}
        sx={{
          gap: 0.75,
          px: 0.5,
          borderRadius: '6px',
          fontFamily: 'inherit',
          fontSize: 13,
          lineHeight: '32px',
          color: theme.palette.grey['800'],
          '&:hover, &:focus-visible': { color: theme.palette.primary.main }
        }}>
        {t('ran.remaining_keys_by_ssid', 'Clés restantes par SSID')}
        <i className={`fa-regular fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ fontSize: 10 }} aria-hidden="true" />
      </ButtonBase>
      <Popover
        id={REMAINING_KEYS_POPOVER_ID}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <Stack component="ul" gap={1} sx={{ listStyle: 'none', m: 0, p: 1.5, minWidth: 220 }}>
          {bySsid.map(({ id, name, count }) => (
            <Stack component="li" key={id} direction="row" justifyContent="space-between" gap={2}>
              <Typography noWrap sx={{ fontSize: 12, color: theme.palette.grey['800'] }}>
                {name}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{loading ? '...' : count ?? '-'}</Typography>
            </Stack>
          ))}
        </Stack>
      </Popover>
    </>
  )
}

const RemainingKeys = ({ count, bySsid, loading, error }) => {
  const { t } = useTranslation()

  if (error) {
    return null
  }

  return (
    <Stack direction={'row'}>
      <SubHeaderInfosIcon>
        <i className={'fa-regular fa-circle-info'} />
      </SubHeaderInfosIcon>
      {bySsid ? (
        <RemainingKeysBySsid bySsid={bySsid} loading={loading} />
      ) : (
        <SubHeaderInfos>{t('ran.remaining_keys', { count: loading ? '...' : count })}</SubHeaderInfos>
      )}
    </Stack>
  )
}

const Ran = () => {
  const { t } = useTranslation()
  const [filters, setFilters] = useFilters()
  const theme = useTheme()
  const navigate = useNavigate()
  const { siteId, roomId } = useParams()
  const [{ selected }] = useSites()
  const [itemsSelected, setItemsSelected] = useItemsSelected()
  const [user] = useUser()
  const apiRans = useRans()
  const searchStarted = useRef(false)
  const outletContext = useOutletContext()
  const { remainingKeys, isStime } = outletContext
  const isEditable = canEditRans({ user, isStime })
  const activeSiteId = useMemo(() => {
    if (selected.length) {
      return selected[selected.length - 1].sf_id
    }
    return siteId
  }, [selected, siteId])

  const handleVerbatimChange = useCallback(
    value => {
      setFilters(prev => ({ ...prev, verbatim: value }))
      const hasSearch = value.trim().length > 2

      if (hasSearch && !searchStarted.current) {
        trackEvent('search_private_wifi')
      }

      searchStarted.current = hasSearch
    },
    [setFilters]
  )

  const handleTopButtonClick = useCallback(
    path => {
      setItemsSelected([])
      navigate(path)
    },
    [navigate, setItemsSelected]
  )

  const pathname = useMemo(() => {
    if (roomId) {
      return `/ran/${activeSiteId}/${roomId}`
    }
    return `/ran/${activeSiteId}`
  }, [activeSiteId, roomId])
  const exportAction = useMemo(
    () => (
      <HeaderExportButton
        analyticsName="private_wifi"
        disabled={Boolean(user.demo_mode)}
        onClick={() => handleTopButtonClick(`${pathname}/export`)}>
        {t('ran.export.button')}
      </HeaderExportButton>
    ),
    [handleTopButtonClick, pathname, t, user.demo_mode]
  )

  useRegisterBreadcrumbAction(exportAction)

  if (selected.length && (!siteId || siteId !== activeSiteId)) {
    return <Navigate to={`/ran/${activeSiteId}`} replace />
  }

  if (!activeSiteId) {
    return <EmptyScreen>{t('ran.no_site_selected')}</EmptyScreen>
  }

  if (remainingKeys.error?.graphQLErrors?.[0]?.extensions?.code === 'NOT_FOUND') {
    return <EmptyScreen>{t('ran.service_not_configured')}</EmptyScreen>
  }

  return (
    <>
      <Stack direction={'row'} section={'ran'} justifyContent={'space-between'}>
        <Stack sx={{ flexDirection: 'row', gap: '8px', mb: 2 }}>
          <TextFilter
            placeholder={t('ran.search_placeholder')}
            value={filters.verbatim}
            onChange={handleVerbatimChange}
          />
          <DateFilter
            placement="right-start"
            onReset={() => {
              setFilters(prev => ({
                ...prev,
                created_start_at: '',
                created_end_at: ''
              }))
            }}
            startDate={filters.created_start_at}
            endDate={filters.created_end_at}
            onStartDateChange={value => {
              setFilters(prev => ({
                ...prev,
                created_start_at: value
              }))
            }}
            onEndDateChange={value => {
              setFilters(prev => ({
                ...prev,
                created_end_at: value
              }))
            }}
            placeholderKey={'coupons.filters.creation_date'}
            showPresets={true}
            blockFutureDates={true}
          />
          <RemainingKeys {...remainingKeys} />
        </Stack>
        {isEditable && (
          <Box
            sx={{
              display: 'flex',
              gap: theme.spacing(1),
              alignItems: 'flex-start'
            }}>
            <Button
              disabled={Boolean(user.demo_mode)}
              tooltiptitle={t('ran.import.button')}
              startIcon={<i className={'fa-regular fa-file-arrow-down'} style={{ fontSize: 14 }} />}
              onClick={() => {
                trackEvent('private_wifi_import', { action_scope: 'page' })
                handleTopButtonClick(`${pathname}/import`)
              }}
              size="12px"
              sx={{
                color: '#7A8096',
                fontWeight: 400,
                fontSize: 12,
                bgcolor: theme.palette.grey[50]
              }}>
              {t('ran.import.button')}
            </Button>
            <Button
              disabled={Boolean(user.demo_mode)}
              onClick={() => {
                trackEvent('private_wifi_create', { action_scope: 'page' })
                handleTopButtonClick(`${pathname}/new`)
              }}
              variant="contained"
              color="primary"
              startIcon={<i className={'fa-regular fa-plus'} />}
              size="12px"
              sx={{
                height: '32px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 500,
                padding: '0 16px 2px 12px',
                fontFamily: 'Poppins'
              }}>
              {t('ran.create.button')}
            </Button>
          </Box>
        )}
      </Stack>
      <MainContent>
        <TableContent>
          <Table apiRans={apiRans} />
        </TableContent>
        {itemsSelected.length === 0 ? <Outlet context={outletContext} /> : <Card />}
      </MainContent>
    </>
  )
}

export default Ran
